import { upsertRoll } from "@/pages/rollshop/repositories/OperationRepository";
import { CommentsField } from "@/shared/components/CommentsField";
import { Roll } from "@/shared/models/Roll";
import { LocationSelector } from "@/shared/selectors/LocationSelector";
import { RollStatusSelector } from "@/shared/selectors/RollStatusSelector";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Button, CircularProgress, Grid2 } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";

type Props = {
	show: boolean;
	roll: Roll | null;
	onHide: (updated?: boolean) => void;
};

export const ChangeRollStatusModal = ({ show, roll, onHide }: Props) => {
	const [statusCode, setStatusCode] = useState<string | null>(null);
	const [locationId, setLocationId] = useState<number | null>(null);
	const [comments, setComments] = useState("");
	const [error, setError] = useState("");

	const upsertMutation = useMutation({
		mutationFn: () =>
			upsertRoll({
				partId: roll!.PartId,
				statusCode,
				locationId,
				comments: comments.trim() ? comments.trim() : undefined,
			}),
		onSuccess: async () => {
			onHide(true);
		},
		onError: (e: Error) => setError(e.message),
	});

	useEffect(() => {
		setStatusCode(roll?.RollStatusCode ?? null);
		setLocationId(roll?.LocationId ?? null);
		setComments(roll?.Comments ?? "");
	}, [roll, show]);

	const hasChanges =
		!!roll &&
		(statusCode !== roll.RollStatusCode ||
			locationId !== (roll.LocationId ?? null) ||
			comments.trim().length > 0);

	const canSave =
		!!roll && !!statusCode && hasChanges && !upsertMutation.isPending;

	return (
		<>
			<MesfModal
				title={`EDIT ROLL: ${roll?.PartName ?? ""}`}
				open={show}
				handleClose={() => onHide()}
				id="change-roll-status-modal"
				maxWidth="sm"
			>
				<MesfModal.Content dividers>
					<Grid2 container spacing={2} justifyContent="center">
						<Grid2 size={{ md: 12, xs: 12 }}>
							<RollStatusSelector
								label="Status"
								value={statusCode}
								onChange={(code: string | null) => setStatusCode(code)}
								required
							/>
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<LocationSelector
								label="Location"
								value={locationId}
								onChange={(id: number | null) => setLocationId(id)}
							/>
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<CommentsField
								value={comments}
								onChange={setComments}
								maxLength={400}
								rows={{ minRows: 3, maxRows: 4 }}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>

				<MesfModal.Actions style={{ padding: "20px 20px 30px" }}>
					<Grid2 container spacing={1} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => onHide()}
								disabled={upsertMutation.isPending}
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								variant="contained"
								color="primary"
								onClick={() => upsertMutation.mutate()}
								disabled={!canSave}
								startIcon={
									upsertMutation.isPending ? (
										<CircularProgress size="1rem" />
									) : undefined
								}
							>
								Save
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>

			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error updating roll"
			/>
		</>
	);
};
